import React, { useEffect, useRef, useState } from "react";

export interface SearchableComboboxOption {
  key: string;
  text: string;
}

export interface UseSearchableComboboxParams {
  options: SearchableComboboxOption[];
  selectedKey?: string;
  /** Called on valid selection OR clear. Passes (undefined, undefined) when cleared. */
  onOptionSelect: (key?: string, text?: string) => void;
  disabled?: boolean;
  loading?: boolean;
  errorMessage?: string;
}

/**
 * Behavioural core of {@link SearchableCombobox}: owns the freeform-filter state,
 * the self-driven keyboard navigation, and all the Fluent v9 quirk workarounds.
 * The component is left as a thin presentation layer over the values returned here.
 */
export default function useSearchableCombobox({
  options,
  selectedKey,
  onOptionSelect,
  disabled = false,
  loading = false,
  errorMessage,
}: UseSearchableComboboxParams) {
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  // Active option index for keyboard navigation. Fluent v9 Combobox in
  // freeform mode leaves the arrow keys bound to the input caret instead of
  // moving the listbox highlight, so we drive the active row ourselves.
  // -1 means "no active row".
  const [activeIndex, setActiveIndex] = useState(-1);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

  // The text to display: prefer active filter, else the selected option's text.
  // The single-option case is NOT auto-selected — the user must always pick
  // explicitly, so the placeholder ("Select") stays visible until they do.
  const selectedOption = options.find((o) => o.key === selectedKey);
  const idleValue = selectedOption?.text ?? "";
  const comboValue = filter ?? idleValue;

  const isError = hasError || !!errorMessage;

  // Apply filter only when the user has actually typed something
  const filteredOptions =
    filter !== undefined && filter !== ""
      ? options.filter((o) => o.text.toLowerCase().includes(filter.toLowerCase()))
      : options;

  // Derived flags
  // Disable entirely when: prop says disabled, loading, OR no options at all (FR-012)
  const isDisabled = disabled || loading || options.length === 0;
  // Allow typing/filtering only when ≥2 options (FR-012); single-option is readonly
  const isFreeform = options.length >= 2;

  // Keep activeIndex within bounds whenever the filtered list shrinks/changes
  useEffect(() => {
    if (activeIndex >= filteredOptions.length) {
      setActiveIndex(filteredOptions.length === 0 ? -1 : filteredOptions.length - 1);
    }
  }, [filteredOptions.length, activeIndex]);

  // Scroll the active option into view when it changes via keyboard
  useEffect(() => {
    if (activeIndex >= 0 && optionRefs.current[activeIndex]) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // ── Internal helpers ───────────────────────────────────────────────────────

  // Fully stop an event so Fluent's merged bubble-phase handlers never run.
  // In React 17 onChangeCapture/onKeyDownCapture are dispatched by SEPARATE native
  // listeners from the bubble ones, so a synthetic stopPropagation() does NOT stop
  // the native bubble event reaching React's root listener. stopImmediatePropagation()
  // on the native event is the only thing that reliably blocks Fluent's handler.
  // Some call sites must NOT preventDefault (e.g. the input change, which would block
  // the caret), hence the flag.
  const blockEvent = (e: React.SyntheticEvent, preventDefault = true) => {
    if (preventDefault) e.preventDefault();
    e.stopPropagation();
    (e.nativeEvent as Event).stopImmediatePropagation();
  };

  // Reset the transient UI state after a value is committed or cleared.
  const resetAfterCommit = () => {
    setFilter(undefined);
    setActiveIndex(-1);
    setHasError(false);
    setOpen(false);
  };

  // Single source of truth for committing a value (or a clear, when key is
  // undefined) and tearing down the transient state. Used by every commit path
  // (option click, clear button, Enter on an active row, Enter on an exact match).
  const commitSelection = (key?: string, text?: string) => {
    onOptionSelect(key, text);
    resetAfterCommit();
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleOpenChange = (_e: unknown, data: { open: boolean }) => {
    if (data.open) {
      // Clear error state whenever the panel reopens
      setHasError(false);
      // Pre-position the keyboard cursor on the currently-selected option so
      // arrow keys continue from the existing value when reopening a populated
      // combobox (instead of restarting from the top).
      const preIdx = selectedKey ? options.findIndex((o) => o.key === selectedKey) : -1;
      setActiveIndex(preIdx);
    } else {
      // When closing without a commit, discard the active filter so the input
      // reverts to the last committed selection (FR-011)
      setFilter(undefined);
      setActiveIndex(-1);
    }
    setOpen(data.open);
  };

  // Fluent v9 fires this when the user clicks an Option row OR the clear button.
  // optionValue === undefined means the clear button was pressed; commitSelection
  // forwards (undefined, undefined) in that case (FR-008 AC02 clear path).
  const handleFluentOptionSelect = (_e: unknown, data: { optionValue?: string; optionText?: string }) => {
    commitSelection(data.optionValue, data.optionText);
  };

  // ── Capture-phase handlers on the input slot ─────────────────────────────
  //
  // WHY CAPTURE PHASE: Fluent v9 merges its internal onChange/onKeyDown handlers
  // with user-provided ones via mergeCallbacks (internal runs first). Placing our
  // handlers in the CAPTURE phase (onChangeCapture, onKeyDownCapture) means they
  // fire BEFORE Fluent's bubble-phase merged handlers. Calling blockEvent() in
  // capture prevents Fluent's bubble-phase logic from running at all.
  //
  // Bug 1 fix: Fluent's useTriggerKeydown runs CloseSelect (Enter→selectOption)
  // before our onKeyDown. By handling Enter in capture with blockEvent we
  // prevent Fluent from auto-selecting the active prefix-match option.
  //
  // Bug 2 fix: Fluent's useInputTriggerSlot.onChange calls clearSelection() when
  // the typed value doesn't prefix-match the current selection, firing
  // onOptionSelect(undefined,undefined) mid-typing. blockEvent in capture
  // prevents that onChange from running entirely.

  const handleInputChangeCapture = (e: React.FormEvent<HTMLInputElement>) => {
    // Block Fluent's bubble-phase onChange → clearSelection chain (Bug 2 fix).
    // preventDefault is intentionally skipped: it would block the native caret.
    blockEvent(e, false);
    setFilter(e.currentTarget.value);
    // Typing always (re)opens the panel so the user can see the filtered
    // results — even right after a selection committed and closed it.
    setOpen(true);
    // Reset keyboard navigation whenever the filter text changes — the user is
    // refining results, not picking from the previous list.
    setActiveIndex(-1);
    // Any input clears the error state (FR-009 AC02)
    setHasError(false);
  };

  const handleInputKeyDownCapture = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Printable characters & Backspace: we OWN text editing instead of letting
    // the browser/Fluent mutate the input value. WHY: right after a selection is
    // committed, Fluent treats the displayed text as a selected option and the
    // first keystroke wipes it (the change event reports an empty value), so the
    // text blanked out and filtering only resumed on the second keystroke.
    // By handling the key ourselves with preventDefault, no native input/change
    // event fires, and we build the filter deterministically from the current
    // value (the committed selection on the first keystroke), so typing APPENDS
    // to the selected text. Only when freeform (>= 2 options) — single-option is
    // read-only and must not filter.
    if (
      options.length >= 2 &&
      (e.key === "Backspace" || (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey))
    ) {
      blockEvent(e);
      const base = filter ?? selectedOption?.text ?? "";
      setFilter(e.key === "Backspace" ? base.slice(0, -1) : base + e.key);
      setOpen(true);
      setActiveIndex(-1);
      setHasError(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      // We fully own arrow navigation. Fluent v9 also binds ArrowUp/Down to its
      // internal activeDescendant controller on the SAME input (bubble phase).
      // In React 17 a synthetic stopPropagation() does NOT stop the native bubble
      // listener at the root, so Fluent would navigate in parallel — two cursors
      // racing, which corrupts the selection committed on Enter and leaves the
      // field flaky afterwards. blockEvent's stopImmediatePropagation() on the
      // native event is the only thing that reliably blocks Fluent's merged handler.
      blockEvent(e);
      if (filteredOptions.length === 0) return;
      if (!open) setOpen(true);
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const next = activeIndex < 0 ? (e.key === "ArrowDown" ? 0 : filteredOptions.length - 1) : activeIndex + delta;
      const clamped = Math.max(0, Math.min(filteredOptions.length - 1, next));
      setActiveIndex(clamped);
      return;
    }
    if (e.key === "Enter") {
      // If the user has navigated to an active row, commit that selection
      // regardless of whether a filter was typed. Fully block Fluent's own
      // CloseSelect (which would also fire selectOption on ITS active option)
      // to avoid a double / conflicting commit.
      if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
        blockEvent(e);
        const chosen = filteredOptions[activeIndex];
        commitSelection(chosen.key, chosen.text);
        return;
      }

      // Nothing typed and no active row: nothing to commit. Block Fluent's
      // CloseSelect so it can't act on a stale internal active option.
      if (filter === undefined) {
        blockEvent(e);
        return;
      }

      // Block Fluent's CloseSelect handler (Bug 1 fix)
      blockEvent(e);

      // Strict case-insensitive exact match — no trim per spec (FR-008 AC01/AC02)
      const exactMatch = options.find((o) => o.text.toLowerCase() === filter.toLowerCase());

      if (exactMatch) {
        // Exact match → commit selection (FR-008 AC02/AC03)
        commitSelection(exactMatch.key, exactMatch.text);
      } else {
        // No exact match → the field has no valid selection. Propagate a clear
        // so the parent resets this field and cascade-clears any dependent
        // fields (e.g. Sheet → Area/Chart). The typed text is kept and shown in
        // red (local filter + error state) so the user sees what was rejected.
        onOptionSelect(undefined, undefined);
        setHasError(true);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      // Escape discards the filter and closes — no error (FR-008 AC04)
      e.stopPropagation();
      setFilter(undefined);
      setActiveIndex(-1);
      setOpen(false);
    }
  };

  return {
    open,
    comboValue,
    filteredOptions,
    activeIndex,
    optionRefs,
    isError,
    isDisabled,
    isFreeform,
    handleOpenChange,
    handleFluentOptionSelect,
    handleInputChangeCapture,
    handleInputKeyDownCapture,
  };
}
